import { ChangeEvent, Fragment, ReactElement } from 'react';

import { ChakraBox, Checkbox } from '@equip.health/ui';

import { MaudPermissions } from '~/lib/constants/permissions.constants';

export type OnUpdateRolesParams<P extends string, R extends string> = {
  permission?: P;
  role: R;
  value: boolean;
};

const TOP_LEVEL_ID = 'admin-user-permissions-tree';

interface PermissionsTreeProps<P extends string, R extends string> {
  onUpdateRoles: (params: OnUpdateRolesParams<P, R>) => void;
  roles: Partial<Record<R, RolesState<P>>>;
}

const PermissionsTree: <P extends string, R extends string>(
  props: PermissionsTreeProps<P, R>,
) => ReactElement<PermissionsTreeProps<P, R>> = <
  P extends string,
  R extends string,
>({
  onUpdateRoles: updateRoles,
  roles,
}: PermissionsTreeProps<P, R>) => {
  const onUpdatePermission =
    (permission: P, role: R) => (evt: ChangeEvent<HTMLInputElement>) =>
      updateRoles({ permission, role, value: evt.target.checked });

  const onUpdateRole = (role: R) => (evt: ChangeEvent<HTMLInputElement>) =>
    updateRoles({ role, value: evt.target.checked });

  return (
    <>
      {Object.entries(roles).map(
        ([
          roleValue,
          {
            description,
            display: roleDisplay,
            isCheckboxHidden,
            isChecked,
            isDisabled,
            isHidden: isRoleHidden,
            permissions,
          },
        ]: [R, RolesState<P>]) => {
          const permissionValues = Object.entries(permissions)
            .filter(
              (permissionEntry: [MaudPermissions, PermissionsState]) =>
                !permissionEntry[1]?.isHidden,
            )
            .map(
              ([, { isChecked }]: [
                MaudPermissions,
                PermissionsState,
              ]): boolean => {
                return isChecked;
              },
            );
          const areAllPermissionsChecked = permissionValues.length
            ? permissionValues.reduce(
                (acc: boolean, isChecked: boolean) => acc && isChecked,
                true,
              )
            : false;
          return (
            !isRoleHidden && (
              <Fragment key={`PermissionsTree::Fragment::${roleValue}`}>
                {!isCheckboxHidden && (
                  <Checkbox
                    description={description}
                    id={`${TOP_LEVEL_ID}__role__${roleDisplay}`}
                    isChecked={
                      permissionValues.length
                        ? areAllPermissionsChecked
                        : isChecked
                    }
                    isDisabled={isDisabled}
                    key={`PermissionsTree::Role::${roleValue}`}
                    label={roleDisplay}
                    onChange={onUpdateRole(roleValue)}
                  />
                )}
                {permissions && Object.keys(permissions).length > 0 && (
                  <ChakraBox
                    key={`PermissionsTree::PermissionsBox::${roleValue}`}
                    paddingLeft={isCheckboxHidden ? undefined : '28px'}
                  >
                    {Object.entries(permissions).map(
                      ([
                        permissionValue,
                        {
                          addendum,
                          display: permissionDisplay,
                          isChecked: permissionIsChecked,
                          isHidden: isPermissionHidden,
                        },
                      ]: [P, PermissionsState]) =>
                        !isPermissionHidden && (
                          <Checkbox
                            addendum={addendum}
                            id={`${TOP_LEVEL_ID}__role__${roleDisplay}__permission__${permissionDisplay}`}
                            isChecked={permissionIsChecked}
                            key={`PermissionsTree::Permission::${permissionValue}`}
                            label={permissionDisplay}
                            marginBottom="8px"
                            onChange={onUpdatePermission(
                              permissionValue,
                              roleValue,
                            )}
                          />
                        ),
                    )}
                  </ChakraBox>
                )}
              </Fragment>
            )
          );
        },
      )}
    </>
  );
};

export default PermissionsTree;
